import React, { createContext, useContext, useState, useEffect } from 'react';
import { generateId } from '../utils/helpers';
import { useCRM } from './CRMContext';
import { useEmail } from './EmailContext';

const SequenceContext = createContext();

export const useSequence = () => useContext(SequenceContext);

export const SequenceProvider = ({ children }) => {
  const { currentUser, emailTemplates, addEmailTemplate } = useCRM();
  const { generateDrafts } = useEmail();

  // --- STATE ---
  const [sequences, setSequences] = useState([]);
  const [runs, setRuns] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [importJobs, setImportJobs] = useState([]);

  // --- MOCK INIT ---
  useEffect(() => {
    const demoSeqId = 'seq-demo-1';
    const complianceSeqId = 'seq-compliance-std';
    const complianceMicrositeSeqId = 'seq-compliance-microsite';

    setSequences([
      {
        sequence_id: demoSeqId,
        name: 'Oppfølging etter møte',
        status: 'Active',
        steps: [
          { step_id: 's1', step_order: 1, type: 'Email', variant: 'Normal', template_id: 'tmpl-1', delay_days: 0 },
          { step_id: 's2', step_order: 2, type: 'Delay', delay_days: 3 },
          { step_id: 's3', step_order: 3, type: 'Email', variant: 'Short', template_id: 'tmpl-2', delay_days: 0 },
        ]
      },
      {
        sequence_id: complianceSeqId,
        name: 'Compliance – Standard',
        project_id: 'proj_standard',
        channel: 'Email',
        status: 'Active',
        steps: [
          { step_id: 'sc-1', step_order: 1, type: 'Email', template_id: 'tmpl-comp-1', variant: 'Normal', delay_days: 0 },
          { step_id: 'sc-2', step_order: 2, type: 'Email', template_id: 'tmpl-comp-2', variant: 'Kort', delay_days: 3 },
          { step_id: 'sc-3', step_order: 3, type: 'Email', template_id: 'tmpl-comp-3', variant: 'Normal', delay_days: 7 },
          { step_id: 'sc-4', step_order: 4, type: 'Email', template_id: 'tmpl-comp-4', variant: 'Kort', delay_days: 14 },
          { step_id: 'sc-5', step_order: 5, type: 'Email', template_id: 'tmpl-comp-5', variant: 'Direkte', delay_days: 21 },
        ]
      },
      {
        sequence_id: complianceMicrositeSeqId,
        name: 'HMS Varsling – Microsite', // Corrected Name
        project_id: 'proj_standard',
        channel: 'Email',
        status: 'Active',
        steps: [
          { step_id: 'scm-1', step_order: 1, type: 'Email', template_id: 'tmpl-comp-1', variant: 'Normal', delay_days: 0 },
          { step_id: 'scm-2', step_order: 2, type: 'Email', template_id: 'tmpl-comp-2', variant: 'Kort', delay_days: 3 },
          { step_id: 'scm-3', step_order: 3, type: 'Email', template_id: 'tmpl-comp-microsite', variant: 'Normal', delay_days: 7 },
          { step_id: 'scm-4', step_order: 4, type: 'Email', template_id: 'tmpl-comp-4', variant: 'Kort', delay_days: 14 },
          { step_id: 'scm-5', step_order: 5, type: 'Email', template_id: 'tmpl-comp-5', variant: 'Direkte', delay_days: 21 },
        ]
      }
    ]);
  }, []);

  // --- ACTIONS ---
  const addSequence = (seq) => {
    const newSeq = { ...seq, sequence_id: generateId('seq'), status: 'Draft', steps: [] };
    setSequences(prev => [...prev, newSeq]);
    return newSeq;
  };

  const updateSequenceSteps = (seqId, steps) => {
    setSequences(prev => prev.map(s => s.sequence_id === seqId ? { ...s, steps } : s));
  };

  // --- IMPORT LOGIC ---
  const parseCSV = (text) => {
    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim());
    
    return lines.slice(1).map((line, idx) => {
      const values = line.split(',').map(v => v.trim());
      const row = { row_no: idx + 1 };
      headers.forEach((h, i) => row[h] = values[i]);
      return row;
    });
  };

  const createImportJob = async (csvText, options) => {
    const jobId = generateId('job');
    const rawRows = parseCSV(csvText);
    
    const rows = rawRows.map(row => ({
      ...row,
      row_id: generateId('row'),
      job_id: jobId,
      validation_status: 'OK',
      validation_errors: ''
    }));

    const newJob = {
      job_id: jobId,
      created_at: new Date().toISOString(),
      status: 'Validert',
      csv_text: csvText,
      options,
      rows,
      summary: {
        rows_total: rows.length,
        rows_valid: rows.length,
        rows_invalid: 0,
        sequences_created: 0
      }
    };

    setImportJobs(prev => [newJob, ...prev]);
    return newJob;
  };

  const runImportJob = async (jobId) => {
    setImportJobs(prev => prev.map(j => j.job_id === jobId ? { ...j, status: 'Fullført' } : j));
  };

  const enrollContact = (contactId, sequenceId) => {
    const seq = sequences.find(s => s.sequence_id === sequenceId);
    if (!seq) throw new Error("Sequence not found");

    const runId = generateId('run');
    const newRun = {
      run_id: runId,
      sequence_id: sequenceId,
      contact_id: contactId,
      status: 'Active',
      current_step_order: 1,
      start_date: new Date().toISOString()
    };
    setRuns(prev => [newRun, ...prev]);

    const firstStep = seq.steps.find(s => s.step_order === 1);
    if (firstStep && firstStep.type === 'Email') {
      const newTask = {
        task_id: generateId('task'),
        run_id: runId,
        type: 'Email',
        status: 'Pending',
        due_date: new Date().toISOString(),
        contact_name: 'Ukjent',
        company_name: 'Ukjent',
        sequence_name: seq.name,
        template_id: firstStep.template_id
      };
      setTasks(prev => [newTask, ...prev]);
    }
    
    return runId;
  };

  const generateDraftFromTask = async (taskId) => {
    return {};
  };

  const completeTask = (taskId) => {
    setTasks(prev => prev.map(t => t.task_id === taskId ? { ...t, status: 'Completed' } : t));
  };

  const addStepToSequence = (seqId, step) => {
    setSequences(prev => prev.map(s => {
      if (s.sequence_id !== seqId) return s;
      const newStep = {
        ...step,
        step_id: generateId('step'),
        step_order: (s.steps?.length || 0) + 1
      };
      return { ...s, steps: [...(s.steps || []), newStep] };
    }));
  };

  return (
    <SequenceContext.Provider value={{
      sequences,
      runs,
      tasks,
      importJobs,
      addSequence,
      updateSequenceSteps,
      enrollContact,
      generateDraftFromTask,
      completeTask,
      addStepToSequence,
      createImportJob,
      runImportJob
    }}>
      {children}
    </SequenceContext.Provider>
  );
};